import React from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { NSCheckbox } from 'bricks';
import { ExtendedColumnDef } from 'components/DraggableTable/types';
import { DraggableTableActionMenu } from '../../../components/DraggableTable/DraggableTableActionMenu/DraggableTableActionMenu';
import { Deal, NamedObject } from '../types';
import {
    COLUMN_OPTIONS, UnitType, SELECT_COLUMN_ID, ACTIONS_COLUMN_ID,
} from '../constants';

const columnHelper = createColumnHelper<Deal>();

export const getNestedValue = (obj: unknown, path: string): any => path.split('.').reduce((acc, key) => {
    if (acc && typeof acc === 'object') {
        return (acc as any)[key];
    }
    return undefined;
}, obj);

export const getDealTableColumns = (selectedUnitType: UnitType): ExtendedColumnDef<Deal>[] => {
    const selectColumn: ColumnDef<Deal> = {
        id: SELECT_COLUMN_ID,
        accessorKey: SELECT_COLUMN_ID,
        header: ({ table }) => (
            <NSCheckbox
                id="select-all"
                checked={table.getIsAllPageRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                callback={() => table.toggleAllRowsSelected()}
                labelText=""
                iconOnly
            />
        ),
        cell: ({ row }) => (
            <NSCheckbox
                id={`row-select-${row.id}`}
                checked={row.getIsSelected()}
                callback={() => row.toggleSelected()}
                labelText=""
                iconOnly
            />
        ),
        enableSorting: false,
    };

    const dynamicColumns = COLUMN_OPTIONS.map(opt => {
        const isUnit = opt.value === 'units';
        const isNested = opt.value.includes('.');

        const cellRenderer = opt.cellRenderer
            ? (info: any) => opt.cellRenderer!(info.row.original)
            : (info: any) => {
                let value;

                if (isUnit) {
                    value = info.row.original[selectedUnitType] || null;
                } else if (isNested) {
                    value = getNestedValue(info.row.original, opt.value);
                } else {
                    value = info.row.getValue(opt.value as keyof Deal);
                }

                return value == null ? '-' : renderCellValue(value);
            };

        const col = isNested
            ? columnHelper.accessor(row => getNestedValue(row, opt.value), {
                id: opt.value,
                header: opt.label,
                cell: cellRenderer,
                enableSorting: opt.sortable,
            })
            : columnHelper.accessor(opt.value as keyof Deal, {
                id: opt.value,
                header: opt.label,
                cell: cellRenderer,
                enableSorting: opt.sortable,
            });

        (col as ExtendedColumnDef<Deal>).includeDropdown = opt.includeDropdown;
        (col as ExtendedColumnDef<Deal>).dropdownOptions = opt.dropdownOptions;
        (col as ExtendedColumnDef<Deal>).alignRight = opt.alignRight;
        return col as ExtendedColumnDef<Deal>;
    });

    const actionsColumn: ColumnDef<Deal> = {
        id: ACTIONS_COLUMN_ID,
        accessorKey: ACTIONS_COLUMN_ID,
        header: '',
        cell: ({ row }) => <DraggableTableActionMenu<Deal> row={row} />,
        enableSorting: false,
    };

    return [selectColumn, ...dynamicColumns, actionsColumn];
};

const renderCellValue = (value: unknown): React.ReactNode => {
    if (value && typeof value === 'object' && 'name' in value) {
        return <span>{(value as NamedObject).name}</span>;
    }
    return <span>{String(value)}</span>;
};
